import React, { useState, useCallback } from "react";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Divider } from "../../design/divider";
import { Select } from "../../design/select";
import { Button } from "../../design/button";
import { useDemographicsData, DemographicCount } from "./hooks/useDemographicsData";
import { ProgramType, PROGRAM_TYPE_LABELS } from "@visitproof/types";
import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";

interface DemographicBarChartProps {
  title: string;
  data: DemographicCount[];
  colorClass?: string;
}

const DemographicBarChart: React.FC<DemographicBarChartProps> = ({ title, data, colorClass = "bg-blue-600" }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="p-6 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
      <Subheading className="mb-4">{title}</Subheading>
      <div className="space-y-3">
        {data.map((item) => (
          <div key={item.key}>
            <div className="flex justify-between text-sm mb-1">
              <span className={`${item.key === "_not_set" ? "italic text-zinc-400 dark:text-zinc-500" : "text-zinc-700 dark:text-zinc-300"}`}>
                {item.label}
              </span>
              <span className="text-zinc-500 dark:text-zinc-400">
                {item.count} ({item.percentage}%)
              </span>
            </div>
            <div className="h-4 bg-zinc-100 dark:bg-zinc-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${item.key === "_not_set" ? "bg-zinc-300 dark:bg-zinc-600" : colorClass} rounded-full transition-all duration-300`}
                style={{ width: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle }) => (
  <div className="p-6 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
    <Text className="text-zinc-500 dark:text-zinc-400">{title}</Text>
    <div className="mt-2 text-3xl font-semibold text-zinc-900 dark:text-white">{value}</div>
    {subtitle && <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</Text>}
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-8">
    <div>
      <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse" />
      <div className="h-4 w-96 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse mt-2" />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="p-6 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
          <div className="h-4 w-24 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse" />
          <div className="h-8 w-16 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse mt-2" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="p-6 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
          <div className="h-6 w-32 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse mb-4" />
          <div className="space-y-3">
            {[...Array(4)].map((_, j) => (
              <div key={j}>
                <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse mb-1" />
                <div className="h-4 bg-zinc-100 dark:bg-zinc-600 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="px-4 py-12 text-center">
    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-100 dark:bg-zinc-800">
      <svg className="w-8 h-8 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    </div>
    <h3 className="mb-2 text-lg font-medium text-zinc-900 dark:text-zinc-100">No demographics data</h3>
    <p className="text-zinc-500 dark:text-zinc-400">
      Add children and cases with demographic information to see statistics here.
    </p>
  </div>
);

const DemographicsPage: React.FC = () => {
  const [programTypeFilter, setProgramTypeFilter] = useState<ProgramType | "all">("all");
  const [yearFilter, setYearFilter] = useState<number | "all">("all");

  const handleProgramTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setProgramTypeFilter(e.target.value as ProgramType | "all");
    },
    []
  );

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setYearFilter(value === "all" ? "all" : parseInt(value, 10));
    },
    []
  );

  const {
    // Totals
    totalChildren,
    totalParents,
    totalCases,
    // Child demographics
    childRace,
    childSex,
    childLanguage,
    childAgeGroups,
    specialNeeds,
    // Parent demographics
    parentRace,
    parentSex,
    parentLanguage,
    parentMaritalStatus,
    parentIncomeLevel,
    parentEducationLevel,
    parentEmploymentStatus,
    parentHousingStatus,
    parentRuralArea,
    parentTranslatorNeeded,
    // Case demographics
    reasonForSupervision,
    presentingConcerns,
    noShowsByParty,
    totalNoShows,
    // Coverage
    childrenWithDemographics,
    parentsWithDemographics,
    casesWithDemographics,
    loading,
    availableYears,
  } = useDemographicsData({ programTypeFilter, yearFilter });

  const exportToCSV = useCallback(() => {
    const sections: { title: string; data: DemographicCount[] }[] = [
      { title: "Child Race/Ethnicity", data: childRace },
      { title: "Child Sex", data: childSex },
      { title: "Child Language", data: childLanguage },
      { title: "Child Age Groups", data: childAgeGroups },
      { title: "Special Needs", data: specialNeeds },
      { title: "Parent Race/Ethnicity", data: parentRace },
      { title: "Parent Sex", data: parentSex },
      { title: "Parent Language", data: parentLanguage },
      { title: "Parent Marital Status", data: parentMaritalStatus },
      { title: "Parent Employment Status", data: parentEmploymentStatus },
      { title: "Parent Housing Status", data: parentHousingStatus },
      { title: "Parent Income Level", data: parentIncomeLevel },
      { title: "Parent Education Level", data: parentEducationLevel },
      { title: "Parent Rural Area", data: parentRuralArea },
      { title: "Parent Translator Needed", data: parentTranslatorNeeded },
      { title: "Reason for Supervision", data: reasonForSupervision },
      { title: "Presenting Concerns", data: presentingConcerns },
      { title: "No-Shows by Party", data: noShowsByParty },
    ];

    const rows: string[] = [];
    // Summary
    rows.push("Demographics Report");
    rows.push(`"Generated","${new Date().toLocaleDateString()}"`);
    if (programTypeFilter !== "all") rows.push(`"Program Filter","${PROGRAM_TYPE_LABELS[programTypeFilter]}"`);
    if (yearFilter !== "all") rows.push(`"Year Filter","${yearFilter}"`);
    rows.push("");
    rows.push(`"Total Cases","${totalCases}"`);
    rows.push(`"Total Children","${totalChildren}"`);
    rows.push(`"Total Parents","${totalParents}"`);
    rows.push("");

    // Each section
    sections.forEach(({ title, data }) => {
      rows.push(`"${title}"`);
      rows.push('"Category","Count","Percentage"');
      data.forEach((item) => {
        rows.push(`"${item.label}","${item.count}","${item.percentage}%"`);
      });
      rows.push("");
    });

    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `demographics-report${yearFilter !== "all" ? `-${yearFilter}` : ""}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [
    childRace, childSex, childLanguage, childAgeGroups, specialNeeds,
    parentRace, parentSex, parentLanguage, parentMaritalStatus,
    parentEmploymentStatus, parentHousingStatus, parentIncomeLevel,
    parentEducationLevel, parentRuralArea, parentTranslatorNeeded,
    reasonForSupervision, presentingConcerns, noShowsByParty,
    totalCases, totalChildren, totalParents, programTypeFilter, yearFilter,
  ]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (totalChildren === 0 && totalParents === 0 && totalCases === 0) {
    return (
      <>
        <Heading>Demographics Report</Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          View demographic statistics for grant reporting purposes.
        </Text>
        <Divider className="my-8" />
        <EmptyState />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Heading>Demographics Report</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            View demographic statistics for grant reporting purposes.
            {(programTypeFilter !== "all" || yearFilter !== "all") && (
              <span className="ml-1 font-medium">
                Filtered by:{" "}
                {[
                  programTypeFilter !== "all" && PROGRAM_TYPE_LABELS[programTypeFilter],
                  yearFilter !== "all" && yearFilter,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
          </Text>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select
            name="year"
            value={yearFilter.toString()}
            onChange={handleYearChange}
            className="sm:w-32"
          >
            <option value="all">All Years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
          <Select
            name="programType"
            value={programTypeFilter}
            onChange={handleProgramTypeChange}
            className="sm:w-48"
          >
            <option value="all">All Programs</option>
            {Object.entries(PROGRAM_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Button onClick={exportToCSV}>
            <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      <Divider className="my-8" />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <StatCard
          title="Total Cases"
          value={totalCases}
          subtitle={`${casesWithDemographics} with reason`}
        />
        <StatCard
          title="Total Children"
          value={totalChildren}
          subtitle={`${childrenWithDemographics} with demographics`}
        />
        <StatCard
          title="Total Parents"
          value={totalParents}
          subtitle={`${parentsWithDemographics} with demographics`}
        />
        <StatCard
          title="Child Coverage"
          value={totalChildren > 0 ? Math.round((childrenWithDemographics / totalChildren) * 100) : 0}
          subtitle="% with data"
        />
        <StatCard
          title="Parent Coverage"
          value={totalParents > 0 ? Math.round((parentsWithDemographics / totalParents) * 100) : 0}
          subtitle="% with data"
        />
        <StatCard
          title="Case Coverage"
          value={totalCases > 0 ? Math.round((casesWithDemographics / totalCases) * 100) : 0}
          subtitle="% with reason"
        />
      </div>

      {/* Child Demographics */}
      <Subheading className="mt-10 mb-6">Child Demographics</Subheading>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DemographicBarChart title="Age Groups" data={childAgeGroups} colorClass="bg-purple-600" />
        <DemographicBarChart title="Race / Ethnicity" data={childRace} colorClass="bg-blue-600" />
        <DemographicBarChart title="Sex" data={childSex} colorClass="bg-green-600" />
        <DemographicBarChart title="Primary Language" data={childLanguage} colorClass="bg-amber-600" />
      </div>

      {/* Special Needs */}
      <Subheading className="mt-10 mb-6">Special Needs</Subheading>
      <div className="grid grid-cols-1 gap-6">
        <DemographicBarChart title="Special Needs Distribution" data={specialNeeds} colorClass="bg-rose-600" />
      </div>

      {/* Parent Demographics */}
      <Subheading className="mt-10 mb-6">Parent Demographics</Subheading>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DemographicBarChart title="Race / Ethnicity" data={parentRace} colorClass="bg-blue-600" />
        <DemographicBarChart title="Sex" data={parentSex} colorClass="bg-green-600" />
        <DemographicBarChart title="Primary Language" data={parentLanguage} colorClass="bg-amber-600" />
        <DemographicBarChart title="Marital Status" data={parentMaritalStatus} colorClass="bg-indigo-600" />
        <DemographicBarChart title="Employment Status" data={parentEmploymentStatus} colorClass="bg-teal-600" />
        <DemographicBarChart title="Housing Status" data={parentHousingStatus} colorClass="bg-cyan-600" />
        <DemographicBarChart title="Income Level" data={parentIncomeLevel} colorClass="bg-emerald-600" />
        <DemographicBarChart title="Education Level" data={parentEducationLevel} colorClass="bg-violet-600" />
        <DemographicBarChart title="Rural Area" data={parentRuralArea} colorClass="bg-orange-600" />
        <DemographicBarChart title="Translator Needed" data={parentTranslatorNeeded} colorClass="bg-pink-600" />
      </div>

      {/* Case Demographics */}
      <Subheading className="mt-10 mb-6">Case Demographics</Subheading>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DemographicBarChart title="Reason for Supervision" data={reasonForSupervision} colorClass="bg-cyan-600" />
        <DemographicBarChart title="Presenting Concerns" data={presentingConcerns} colorClass="bg-rose-600" />
      </div>

      {/* Visit No-Shows */}
      <Subheading className="mt-10 mb-6">Visit No-Shows</Subheading>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          title="Total No-Shows"
          value={totalNoShows}
          subtitle="across all cases"
        />
      </div>
      {totalNoShows > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DemographicBarChart title="No-Shows by Responsible Party" data={noShowsByParty} colorClass="bg-amber-600" />
        </div>
      )}
    </>
  );
};

export default DemographicsPage;
